import { useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { ClubBook } from "@pagemates/shared";
import { api, describeError } from "../../lib/api";
import { Spinner } from "../../components/ui";
import { BookSearch, type SearchNote } from "../../components/BookSearch";
import { ChevronIcon, PlusIcon } from "../../components/Icons";
import { NoteEditor, SuggestionRow, useSuggestionRefresh } from "../../components/Suggestion";
import { useClub } from "./ClubLayout";
import { BackBar } from "../../components/ui";
import { BookPanel } from "./ShelfTab";

/*
 * The Suggestions page (PLAN.md §5b): where the club reviews what it might
 * read. Everyone's: New this week first, so a fresh suggestion gets seen
 * before it has taps; then Most wanted, picking up after the books on deck,
 * with a line where the kept top ends; then Past. Mine: your suggestions with
 * their place and any warning, and yours in Past with the one bump.
 */
export function SuggestionsPage() {
  const { clubId, club } = useClub();
  const c = club.data!;
  const navigate = useNavigate();
  const { book: openId = null, mine: mineView = false, add = false } = useSearch({ from: "/authed/clubs/$clubId/suggestions" });
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const q = useQuery({ queryKey: ["suggestions", clubId], queryFn: () => api.suggestions(clubId) });
  const books = useQuery({ queryKey: ["books", clubId], queryFn: () => api.clubBooks(clubId) });
  const refresh = useSuggestionRefresh(clubId);
  const [text, setText] = useState("");
  const [added, setAdded] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<ClubBook | null>(null);
  const setSearch = (s: { book?: string; mine?: boolean; add?: boolean }) =>
    void navigate({ to: "/clubs/$clubId/suggestions", params: { clubId }, search: { ...(s.book ? { book: s.book } : {}), ...(s.mine ? { mine: true } : {}), ...(s.add ? { add: true } : {}) }, replace: true });
  const addBook = useMutation({
    mutationFn: (hc: number) => api.addBook(clubId, hc),
    onSuccess: async (b) => { await refresh(); setAdded(b.duplicate ? `Added your interest in ${b.book.title}.` : null); setJustAdded(b.duplicate ? null : b); setSearch({ mine: mineView }); },
  });
  const isAdmin = c.myRole !== "member";
  const canAdd = isAdmin || c.settings.onDeckPolicy === "everyone";
  const meId = me.data?.id;
  const turn = books.data?.turn ?? null;
  const canStart = isAdmin || (!!turn && turn.user.id === meId);

  const all = useMemo(() => q.data ? [...q.data.running, ...q.data.past] : [], [q.data]);
  if (q.isPending) return <main className="page"><Spinner /></main>;
  if (q.isError) return <main className="page"><p className="error">{describeError(q.error)}</p></main>;
  const d = q.data;
  const open = openId ? all.find((b) => b.id === openId) ?? null : null;
  const t = text.trim().toLowerCase();
  const matches = (b: ClubBook) => !t || b.book.title.toLowerCase().includes(t) || b.book.authors.some((a) => a.toLowerCase().includes(t));
  const beyondDeck = d.running.filter((b) => !b.suggestion?.onDeck);
  const fresh = beyondDeck.filter((b) => b.suggestion?.isNew);
  const wanted = beyondDeck.filter((b) => !b.suggestion?.isNew);
  const mineRunning = d.running.filter((b) => b.addedBy?.id === meId);
  const minePast = d.past.filter((b) => b.addedBy?.id === meId);
  const current = books.data?.current ?? null;
  // What the search knows: already in the running, in Past, being read now, or read before.
  const noteFor = (hc: number): SearchNote | null => {
    if (current?.book.hardcoverId === hc) return { disabled: true, action: "Reading now" };
    const r = d.running.find((b) => b.book.hardcoverId === hc);
    if (r) return { action: r.suggestion?.mine ? "Wanted" : "Want to read", disabled: r.suggestion?.mine, note: `Already ${r.suggestion?.onDeck ? "on deck" : "in the running"}${r.addedBy ? `, suggested by ${r.addedBy.id === meId ? "you" : r.addedBy.name}` : ""}` };
    const p = d.past.find((b) => b.book.hardcoverId === hc);
    if (p) return { action: "Bring it back", note: `In Past, suggested by ${p.addedBy?.id === meId ? "you" : p.addedBy?.name ?? "someone"}` };
    const read = books.data?.finished.find((b) => b.book.hardcoverId === hc);
    if (read?.finishedAt) return { note: `The club read this in ${new Date(read.finishedAt).getFullYear()}. You can suggest a re-read.` };
    return null;
  };
  const row = (b: ClubBook, showRank = false) => (
    <SuggestionRow key={b.id} clubId={clubId} b={b} meId={meId} isAdmin={isAdmin} canStart={canStart} showRank={showRank}
      onOpen={() => setSearch({ book: b.id, mine: mineView })}
      onStart={() => setSearch({ book: b.id, mine: mineView })} /* starting asks first: it happens in the book's pane */ />
  );
  const keep = d.keep;
  // Most wanted, with a quiet line where the kept top ends (only when Past is on).
  const wantedRows: React.ReactNode[] = [];
  wanted.filter(matches).forEach((b, i, arr) => {
    wantedRows.push(row(b));
    const next = arr[i + 1];
    if (keep !== null && b.suggestion?.rank !== null && b.suggestion!.rank! <= keep && next && (next.suggestion?.rank ?? 0) > keep) {
      wantedRows.push(<div key="cut" className="cut">kept in the running above this line · {keep} books</div>);
    }
  });

  return (
    <main className={`page stack suggestions${open ? " with-panel" : ""}`}>
      <BackBar label="Shelf" link={{ to: "/clubs/$clubId/shelf", params: { clubId } }}
        right={canAdd && !add ? <button type="button" className="btn primary sm" onClick={() => { setAdded(null); setSearch({ add: true, mine: mineView }); }}><PlusIcon width={16} height={16} />Add a book</button> : undefined} />
      <div className="row spread" style={{ flexWrap: "wrap", gap: 10 }}>
        <h1>Suggestions</h1>
        <div className="seg" role="radiogroup" aria-label="Whose suggestions">
          <button type="button" aria-pressed={!mineView} onClick={() => setSearch({})}>Everyone's</button>
          <button type="button" aria-pressed={mineView} onClick={() => setSearch({ mine: true })}>Mine</button>
        </div>
      </div>

      {add && (
        <div className="card stack-sm">
          <div className="row spread"><h3>Add a book</h3><button className="btn ghost sm" onClick={() => setSearch({ mine: mineView })}>Done</button></div>
          {d.mine.cap !== null && <p className="muted small" style={{ margin: 0 }}>You have {d.mine.active} of {d.mine.cap} suggestions going.</p>}
          <BookSearch onPick={(b) => addBook.mutate(b.hardcoverId)} picking={addBook.isPending ? addBook.variables : null} noteFor={noteFor} />
          {addBook.isError && <p className="error small">{describeError(addBook.error)}{addBook.error && describeError(addBook.error).includes("Move one") && <> <button type="button" className="linkish" onClick={() => setSearch({ mine: true })}>See yours</button></>}</p>}
        </div>
      )}
      {added && !add && <p className="ok small" style={{ margin: 0 }}>{added}</p>}
      {justAdded && !add && (
        <div className="card stack-sm">
          <div className="small"><b>Suggested {justAdded.book.title}.</b></div>
          <NoteEditor clubId={clubId} b={justAdded} prompt="Tell the club why, in a line. It shows inside the cover and on the book." onDone={() => setJustAdded(null)} />
        </div>
      )}

      <input id="sugg-search" className="input" type="search" placeholder="Search suggestions and Past" value={text} onChange={(e) => setText(e.target.value)} autoComplete="off" />

      {!mineView ? (
        <>
          {fresh.filter(matches).length > 0 && (
            <section className="stack-sm">
              <div className="sect"><h3>New this week</h3><span className="count">{fresh.length}</span></div>
              <div className="deck">{fresh.filter(matches).map((b) => row(b))}</div>
            </section>
          )}
          <section className="stack-sm">
            <div className="sect"><h3>Most wanted</h3><span className="count">{d.running.some((b) => b.suggestion?.onDeck) ? `after the ${d.running.filter((b) => b.suggestion?.onDeck).length} on deck` : "in the running"}</span></div>
            {wantedRows.length > 0 ? <div className="deck">{wantedRows}</div>
              : <p className="muted small">{t ? "Nothing in the running matches." : d.running.length === 0 ? "Nothing suggested yet. Add the first book." : "Everything in the running is on deck or new this week."}</p>}
          </section>
          {d.past.length > 0 && (
            <details className="fold past" open={Boolean(t)}>
              <summary><span className="sect"><h3>Past suggestions</h3><span className="count">{d.past.length}{keep !== null ? ` · quiet for ${c.settings.retireDays} days` : ""}</span></span><ChevronIcon className="chev" width={16} height={16} aria-hidden="true" /></summary>
              <div className="deck" style={{ marginTop: 8 }}>{d.past.filter(matches).map((b) => row(b))}</div>
            </details>
          )}
        </>
      ) : (
        <>
          <section className="stack-sm">
            <div className="sect"><h3>Yours</h3><span className="count">{d.mine.cap !== null ? `${d.mine.active} of ${d.mine.cap} going` : `${mineRunning.length}`}</span></div>
            {mineRunning.filter(matches).length > 0 ? <div className="deck">{mineRunning.filter(matches).map((b) => row(b, true))}</div>
              : <p className="muted small">You haven't suggested anything that's still in the running.</p>}
          </section>
          {minePast.length > 0 && (
            <section className="stack-sm">
              <div className="sect"><h3>Yours in Past</h3><span className="count">{minePast.length}</span></div>
              <div className="deck">{minePast.filter(matches).map((b) => row(b))}</div>
            </section>
          )}
        </>
      )}

      {open && <BookPanel key={open.id} b={open} clubId={clubId} isAdmin={isAdmin} canStart={canStart} meId={meId} current={current} onChange={refresh} onClose={() => setSearch({ mine: mineView })} />}
    </main>
  );
}
